import { CSSProperties, ReactNode, useLayoutEffect, useRef } from "react";
import { ChapterMarker } from "../Section/Section";

/** Design space every canvas scene is laid out in. */
export const DESIGN_W = 1200;
export const DESIGN_H = 720;
/** Top band of the design space that sits under the fixed navbar. */
const NAV_BAND = 56;
/** Upper bound on enlargement, so a huge monitor doesn't blow the type up. */
const MAX_SCALE = 1.1;

interface SceneCanvasProps {
    /** "r, g, b" — the scene's light colour, exposed as `--scene-tint`. */
    tint: string;
    marker?: { index: number; label: string };
    children: ReactNode;
    /** Design space. Defaults to the desktop 1200×720; phone scenes use a portrait one. */
    width?: number;
    height?: number;
    /** Top band of the design space that sits under the fixed navbar. */
    navBand?: number;
    /** Horizontal breathing room kept around the canvas, in screen px. */
    pad?: number;
    className?: string;
}

/**
 * A fixed 1200×720 design space, scaled to fit the pinned stage.
 *
 * Scenes place every element in design pixels. That keeps each morph's
 * geometry exactly as designed at every viewport size story mode runs at
 * (≥1024×720), instead of re-deriving dozens of FLIP targets from a reflowing
 * layout. Scaling is one transform on this element, recomputed on resize.
 */
export default function SceneCanvas({
    tint,
    marker,
    children,
    width = DESIGN_W,
    height = DESIGN_H,
    navBand = NAV_BAND,
    pad = 48,
    className = "",
}: SceneCanvasProps) {
    const ref = useRef<HTMLDivElement>(null);

    useLayoutEffect(() => {
        const el = ref.current;
        const stage = el?.parentElement;
        if (!el || !stage) return;

        const fit = () => {
            const W = stage.clientWidth;
            const H = stage.clientHeight;
            const nav = (document.querySelector(".navbar") as HTMLElement | null)?.offsetHeight ?? 64;
            const usableH = height - navBand;
            const s = Math.min(MAX_SCALE, (W - pad) / width, (H - nav - 8) / usableH);
            const top = nav + Math.max(0, (H - nav - usableH * s) / 2) - navBand * s;
            el.style.transform = `translate(${(W - width * s) / 2}px,${top}px) scale(${s})`;
        };

        fit();
        const ro = new ResizeObserver(fit);
        ro.observe(stage);
        return () => ro.disconnect();
    }, [width, height, navBand, pad]);

    return (
        <div
            ref={ref}
            className={`scene-canvas ${className}`}
            style={{ "--scene-tint": tint, width, height } as CSSProperties}
        >
            {marker && (
                <div className="scene-marker">
                    <ChapterMarker index={marker.index} label={marker.label} />
                </div>
            )}
            {children}
        </div>
    );
}
